import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Pencil, Check, X, Plus, ListOrdered } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SequencesDialog = ({ open, onOpenChange, onChanged }) => {
  const [sequences, setSequences] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({ code: '', name: '' });
  const [newSeq, setNewSeq] = useState({ code: '', name: '' });
  const [saving, setSaving] = useState(false);

  const fetchSequences = useCallback(async () => {
    try {
      const resp = await axios.get(`${API_URL}/sequences`);
      setSequences(resp.data);
    } catch (err) { /* silencieux */ }
  }, []);

  useEffect(() => { if (open) fetchSequences(); }, [open, fetchSequences]);

  const startEdit = (s) => {
    setEditing(s.code);
    setEditValues({ code: s.code, name: s.name });
  };

  const saveEdit = async (oldCode) => {
    setSaving(true);
    try {
      const resp = await axios.put(`${API_URL}/sequences/${encodeURIComponent(oldCode)}`, editValues);
      toast.success(`Séquence mise à jour${resp.data.modules_modifies > 0 ? ` (${resp.data.modules_modifies} module(s) modifié(s))` : ''}`);
      setEditing(null);
      fetchSequences();
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const createSequence = async () => {
    if (!newSeq.code.trim() || !newSeq.name.trim()) {
      toast.error('Renseignez le code et le nom de la séquence');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/sequences`, newSeq);
      toast.success(`Séquence ${newSeq.code} créée`);
      setNewSeq({ code: '', name: '' });
      fetchSequences();
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sequences-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListOrdered className="w-5 h-5 text-indigo-600" /> Gestion des séquences
          </DialogTitle>
          <DialogDescription>
            Renommer une séquence met à jour automatiquement tous les modules qui l'utilisent.
          </DialogDescription>
        </DialogHeader>

        {/* Création */}
        <div className="border rounded-lg p-3 bg-slate-50/60 space-y-2">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Nouvelle séquence</p>
          <div className="flex gap-2">
            <Input
              value={newSeq.code}
              onChange={(e) => setNewSeq({ ...newSeq, code: e.target.value })}
              placeholder="Code (ex: SEQ-01)"
              className="w-36"
              data-testid="input-new-seq-code"
            />
            <Input
              value={newSeq.name}
              onChange={(e) => setNewSeq({ ...newSeq, name: e.target.value })}
              placeholder="Nom de la séquence"
              className="flex-1"
              data-testid="input-new-seq-name"
            />
            <Button size="sm" onClick={createSequence} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-create-sequence">
              <Plus className="w-4 h-4 mr-1" /> Créer
            </Button>
          </div>
        </div>

        {/* Liste */}
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-[130px_1fr_100px_90px] bg-slate-100 px-3 py-2 text-[10px] font-medium text-slate-500 uppercase tracking-wide">
            <span>Code</span>
            <span>Nom</span>
            <span className="text-center">Modules</span>
            <span className="text-center">Action</span>
          </div>
          <div className="divide-y max-h-[340px] overflow-y-auto">
            {sequences.map(s => (
              <div key={s.code} className="grid grid-cols-[130px_1fr_100px_90px] items-center px-3 py-2" data-testid={`sequence-row-${s.code}`}>
                {editing === s.code ? (
                  <>
                    <Input value={editValues.code} onChange={(e) => setEditValues({ ...editValues, code: e.target.value })} className="h-7 text-xs mr-2" data-testid="input-edit-seq-code" />
                    <Input value={editValues.name} onChange={(e) => setEditValues({ ...editValues, name: e.target.value })} className="h-7 text-xs mr-2" data-testid="input-edit-seq-name" />
                    <span className="text-center text-xs text-slate-500">{s.nb_modules}</span>
                    <div className="flex justify-center gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-600" onClick={() => saveEdit(s.code)} disabled={saving} data-testid="btn-save-sequence">
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400" onClick={() => setEditing(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-xs font-mono font-medium text-indigo-700">{s.code}</span>
                    <span className="text-sm text-slate-800 truncate pr-2">{s.name || <span className="text-slate-300">Sans nom</span>}</span>
                    <span className="text-center">
                      <Badge variant="outline" className={`text-[10px] ${s.nb_modules === 0 ? 'text-slate-400' : 'text-slate-600'}`}>{s.nb_modules}</Badge>
                    </span>
                    <div className="flex justify-center">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600" onClick={() => startEdit(s)} data-testid={`btn-edit-sequence-${s.code}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
            {sequences.length === 0 && (
              <p className="text-center py-8 text-sm text-slate-400">Aucune séquence pour le moment</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
